/**
 * Reine Hilfen für den Reiter „Leistungskatalog“ — der Baum kommt fertig vom Server, hier wird er nur
 * aufgeklappt, durchsucht und beschriftet.
 */

import type { CatalogueCrosswalk, CatalogueNode } from "../../BaseliningAPI";

export interface VisibleRow {
  node: CatalogueNode;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
}

const normalise = (text: string): string =>
  text
    .toLocaleLowerCase("de")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

/** Ob ein Knoten selbst zur Suche passt: Bezeichnung, englischer Name, Schlüssel oder ein Code. */
export const nodeMatches = (node: CatalogueNode, query: string): boolean => {
  const q = normalise(query);
  if (!q) return true;
  const haystack = [node.name, node.nameEn ?? "", node.key, ...node.crosswalk.map((c) => c.code)].map(normalise);
  return haystack.some((h) => h.includes(q));
};

/**
 * Die Zeilen, die der Baum zeigt. Ohne Suche: was aufgeklappt ist. Mit Suche: jeder passende Knoten
 * samt seiner Vorfahren (sonst stünde er ohne Zusammenhang da), aufgeklappt, egal was vorher offen war.
 */
export const visibleRows = (roots: CatalogueNode[], expanded: Set<string>, query: string): VisibleRow[] => {
  const searching = normalise(query) !== "";
  const rows: VisibleRow[] = [];

  const keep = new Map<string, boolean>();
  const mark = (node: CatalogueNode): boolean => {
    const childHit = node.children.map(mark).some(Boolean);
    const hit = nodeMatches(node, query) || childHit;
    keep.set(node.id, hit);
    return hit;
  };
  if (searching) roots.forEach(mark);

  const walk = (nodes: CatalogueNode[], depth: number) => {
    for (const node of nodes) {
      if (searching && !keep.get(node.id)) continue;
      const isOpen = searching || expanded.has(node.id);
      rows.push({ node, depth, hasChildren: node.children.length > 0, expanded: isOpen && node.children.length > 0 });
      if (isOpen) walk(node.children, depth + 1);
    }
  };
  walk(roots, 0);
  return rows;
};

/** Der Code eines Knotens in einem Schema — bei mehreren (je Leistungsart) der für „alle“ zuerst. */
export const primaryCode = (node: CatalogueNode, scheme: string): CatalogueCrosswalk | undefined => {
  const entries = node.crosswalk.filter((c) => c.scheme === scheme);
  return entries.find((c) => !c.activity) ?? entries[0];
};

/** Alle Knoten-Ids eines Baums — für „alles aufklappen“. */
export const allIds = (roots: CatalogueNode[]): string[] =>
  roots.flatMap((n) => [n.id, ...allIds(n.children)]);

export const countNodes = (roots: CatalogueNode[]): number => allIds(roots).length;

export const SCHEME_LABELS: Record<string, string> = {
  gefma200: "GEFMA 200",
  gefma100: "GEFMA 100",
  din18960: "DIN 18960",
  gefma520: "GEFMA 520",
  "en15221-4": "EN 15221-4",
  betrkv: "BetrKV",
};
